/**
 * Utility functions for time formatting and manipulation
 */

/**
 * Formats a date as relative time (e.g., "2 minutes ago", "3 days ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const past = new Date(date);
  const diffInMs = now.getTime() - past.getTime();
  
  // Convert to different units
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  // Handle future dates
  if (diffInMs < 0) {
    return 'just now';
  }

  // Less than a minute
  if (diffInMinutes < 1) {
    return 'just now';
  }

  // Minutes
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  }

  // Hours
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }

  // Days
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }

  // Weeks
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  }

  // Months
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
  }

  // Years
  return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
}

/**
 * Formats a date as a short relative time (e.g., "2m", "3d", "1w")
 */
export function formatShortRelativeTime(date: string | Date): string {
  const now = new Date();
  const past = new Date(date);
  const diffInMs = now.getTime() - past.getTime();
  
  // Convert to different units
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);

  // Handle future dates
  if (diffInMs < 0) {
    return 'now';
  }

  // Less than a minute
  if (diffInMinutes < 1) {
    return 'now';
  }

  // Minutes
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }

  // Hours
  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  // Days
  if (diffInDays < 7) {
    return `${diffInDays}d`;
  }

  // Weeks
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w`;
  }

  // Months
  return `${diffInMonths}mo`;
}

/**
 * Normalizes a timestamp to a valid ISO string
 */
export function normalizeTimestamp(timestamp: string | number | Date | null | undefined): string {
  if (!timestamp) {
    return new Date().toISOString();
  }

  if (typeof timestamp === 'string' && !isNaN(Date.parse(timestamp))) {
    return timestamp;
  }

  if (typeof timestamp === 'number' && !isNaN(timestamp)) {
    return new Date(timestamp).toISOString();
  }

  if (timestamp instanceof Date && !isNaN(timestamp.getTime())) {
    return timestamp.toISOString();
  }

  console.warn('Invalid timestamp detected, using current time', { invalidTimestamp: timestamp });
  return new Date().toISOString();
}

/**
 * Formats a timestamp for display in list components (favorites, activity, etc.)
 */
export function formatRelativeTimestamp(timestamp: string): string {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Formats a duration in milliseconds to a compact human-readable string.
 * Examples: "2s", "5m 30s", "1h 15m"
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return '0s';

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Formats a time range as "HH:MM – HH:MM"
 */
export function formatTimeRange(start: string, end: string): string {
  const fmt = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${fmt(new Date(start))} – ${fmt(new Date(end))}`;
}
